import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { setStoreName, fetchStore, selectDiscountCode } from '../webshop/globalSlice';
import MenuSlider from '../../components/MenuSlider';
import IncarnateModal from '../../components/IncarnateModal';
import HeaderContainer from './HeaderContainer';
import FooterContainer from './Footer';
import ScrollToTop from '../../utils/browserScroll';
import SendInvite from '../invites/components/SendInvite';
import { useInvitesModalState } from '../invites/hooks/useInvitesState';
import { useModule, useWebshopName } from '../../hooks/readFromUrls';
import LoginModal from '../auth/components/LoginModal';
import { useNoHeaderModules } from '../../hooks/noHeaderModules';
import ManageCookiesBanner from '../manageCookiesBanner';
import WarningMessages from './WarningMessages';
import { Toolbar } from '@mui/material';
import NewFeatures from '../newFeatures';
import LoadingDots from '../../ui/Loading/LoadingDots';
import { useIsLoggedIn, useAuthLoading, useAuthToken } from '../auth/hooks';
import { rootApi } from '../../services/rootApi';
import GiftCodeHandler from '../../components/GiftCodeHandler';
import GATracker from '../../helpers/GATracker';

const ContentWrapp = styled.div`
  height: ${(props) => (props.$noHeader ? '100vh' : `calc(100vh - ${props.theme.headerSize})`)};
  width: 100%;
`;
const WenshopContent = styled.div`
  min-height: ${(props) => `calc(100vh - ${props.theme.headerSize} - ${props.theme.footerSize})`};
  width: 100%;
`;

const Wrapper = styled.div`
  .MuiToolbar-root {
    height: ${(props) => props.theme.headerSize};
    min-height: ${(props) => props.theme.headerSize};
  }
`;

const WrappContainer = ({ children }) => {
  const dispatch = useDispatch();
  const { modalState } = useInvitesModalState();
  const isInviteModalOpen = modalState?.isInviteModalOpen || false;

  const module = useModule();
  const noHeader = useNoHeaderModules();
  const webshop = useWebshopName();
  const discountCode = useSelector(selectDiscountCode);

  const { isAuthLoading } = useAuthLoading();
  const loggedIn = useIsLoggedIn();
  const authToken = useAuthToken();

  // One-time prefetch after auth settles; do not subscribe (avoid navigation refetch)
  // IMPORTANT: Check that token exists before prefetching to prevent race condition
  // where loggedIn=true but token not yet persisted (e.g., during login flow)
  const prefetchedRef = React.useRef(false);
  useEffect(() => {
    if (!prefetchedRef.current && loggedIn && !isAuthLoading && authToken) {
      prefetchedRef.current = true;
      dispatch(rootApi.endpoints.fetchSettings.initiate(undefined, { subscription: false }));
      dispatch(rootApi.endpoints.fetchStoreCurrency.initiate(undefined, { subscription: false }));
    }
  }, [loggedIn, isAuthLoading, authToken, dispatch]);

  useEffect(() => {
    if (module !== 'store' && module !== 'store_edit') return;
    dispatch(setStoreName(webshop));
    const args = { store: webshop };
    // Discount context is only meaningful in the public storefront view.
    if (module === 'store') args.discountCode = discountCode;
    dispatch(fetchStore(args));
  }, [webshop, discountCode, dispatch, module]);

  const ContentContainer = useMemo(() => (module === 'store' ? WenshopContent : ContentWrapp), [module]);
  // Show loading state until auth is checked
  if (isAuthLoading && !loggedIn) {
    return <LoadingDots showLogo={true} message={'Verifying authentication'} fullContainer={true} />;
  }

  return (
    <Wrapper>
      {loggedIn && <MenuSlider />}
      <IncarnateModal />
      {isInviteModalOpen && <SendInvite />}
      <LoginModal />
      <GiftCodeHandler />
      <NewFeatures enabled={false} />
      <HeaderContainer noHeader={noHeader} />
      <ScrollToTop />
      {/* <Cart /> */}
      {!noHeader && <Toolbar />}
      <ContentContainer $noHeader={noHeader}>
        <WarningMessages neverShow={noHeader || module === 'store' || module === 'view' || module === 'edit'} />
        {/* <BetaEditorBanner neverShow={module !== 'dashboard'} /> */}
        {children}
      </ContentContainer>
      {module === 'store' && <FooterContainer />}
      <GATracker isStoreRoute={module === 'store'} />
      <ManageCookiesBanner />
    </Wrapper>
  );
};

export default WrappContainer;
